/** The customer's words - **U8, design uplift 2026-11-03**: the ONE table that turns an internal code or a
 * server string into what a finance lead reads.
 *
 * Three reviewers (accessibility, enterprise UX, persona walk-through) found the same thing from three
 * directions: register codes (`C-05 is DISPUTED`), decision numbers (`ADR-0105 (B-12)`), event codes
 * (`applicability_determined`, `engine no person`), operations codes (`ops.anchor_never_run`), region and
 * factor-set ids (`eu-west-2`, `us_epa_2025_v2`), a support level spelled as the ADR spells it, and words
 * only the build team uses (*census*, *windows*, *declared nil*, *gross CV*) all reached customer screens.
 * Each was true. Together they read as a developer console.
 *
 * **What this file is, and is not.**
 *
 * - It is wording. It changes how a fact is *said*, never the fact: the server's support level is still
 *   `regimes/support.py`'s, a regime rule's text is still `definitions.py`'s (changing that text is a
 *   `RegimeSpec.version` bump, so the screen maps it here instead), and a ledger entry's type is still
 *   the string the chain hashed. The raw code stays available where an auditor needs it: a `title`, a
 *   `data-` attribute, the CSV export - never as the visible value.
 * - Every map has a **fallback that is still plain**: a code this table does not know is spoken
 *   (`humanise`), not printed. A new ledger entry type is therefore never shown as `new_thing.happened`,
 *   and `test_customer_words_cover_the_server` (backend) fails until it also has a sentence here.
 * - `scrubInternal` is the net under the table: it removes a decision number or a register id from any
 *   server sentence that carries one, so a string the table has never seen still reaches the screen
 *   without a code in it. It is a safety net, not the plan: where the sentence is ours (an Admin row, an
 *   info text) the sentence was rewritten.
 *
 * `customerWords.test.ts` holds the tables; `noInternalTerms.test.ts` scans every authored string on the
 * customer screens and fails for a decision number or a register id.
 */

// ---------------------------------------------------------------------------------- small helpers

/** A code spoken: `applicability_determined` and `ops.anchor_never_run` become sentence-case words.
 * The fallback of every table below, so an unknown code is never shown as itself. */
export function humanise(code: string): string {
  const spoken = code.replace(/[._]+/g, " ").replace(/\s+/g, " ").trim();
  return spoken.length === 0 ? spoken : spoken.charAt(0).toUpperCase() + spoken.slice(1);
}

/** First letter upper case, the rest as it stands: chips and tile values are sentence case. */
export function sentenceCase(text: string): string {
  return text.length === 0 ? text : text.charAt(0).toUpperCase() + text.slice(1);
}

/** A decision-register id (`C-05`, `B-19`, `U-17`, `EXP-22`, `C-02a`): a letter or three and two digits. */
export const REGISTER_ID = /\b[A-Z]{1,4}-\d{2}[a-z]?\b/;
/** A decision-record number (`ADR-0105`). */
export const DECISION_NUMBER = /\bADR-\d+\b/;

// ---------------------------------------------------------------------------------- the net

/** Remove a decision number or a register id from a server sentence, and say the sentence without it.
 *
 * Order matters and each step is one thing a real server string does:
 *
 * 1. `ADR-0105 (B-12). Each person ...` - a lead-in that is only codes.
 * 2. `(ADR-0081 option A)`, `(verification log C-08)`, `(U-03, U-15)`, `(U-17, Not read)` - a bracket
 *    that is only a citation of ours.
 * 3. `C-05 is DISPUTED` - the dispute said as what it is.
 * 4. Anything left (`See B-19.`, `ADR-0020`) is dropped with its connecting word, so no code survives.
 *
 * It does not touch a citation of the *law* (`SI 2008/410 Sch 7 para 20D`, `§38532`): those are what the
 * customer is entitled to read and are not ours. */
export function scrubInternal(text: string): string {
  let out = text;
  // 1. A lead-in made only of codes: "ADR-0105 (B-12). " / "ADR-0081: ".
  out = out.replace(/(^|[.!?]\s+)ADR-\d+(?:\s+option\s+[A-Z])?(?:\s*\([A-Z]{1,4}-\d{2}[a-z]?\))?[.:]\s*/g, "$1");
  // 2. A bracket that only cites our own records.
  out = out.replace(/\s*\((?:[^()]*\b(?:ADR-\d+|verification log)\b[^()]*)\)/g, "");
  out = out.replace(
    /\s*\((?:(?:see\s+)?(?:[A-Z]{1,4}-\d{2}[a-z]?)(?:\s*(?:,|;|and)\s*(?:[A-Z]{1,4}-\d{2}[a-z]?))*)(?:,\s*[^()]*)?\)/g,
    "",
  );
  // 3. The dispute, said as what it is.
  out = out.replace(/\b[A-Z]{1,4}-\d{2}[a-z]?\s+is\s+DISPUTED\b/g, "The sources disagree");
  out = out.replace(/\bDISPUTED\b/g, "disputed");
  // 4. A code left in running prose: drop it with the word that introduced it.
  out = out.replace(/\s*(?:,\s*)?\b(?:see|in|under|per|as in)\s+(?:ADR-\d+|[A-Z]{1,4}-\d{2}[a-z]?)\b/g, "");
  out = out.replace(/\s*\bADR-\d+(?:\s+option\s+[A-Z])?\b/g, "");
  out = out.replace(/\s*\b[A-Z]{1,4}-\d{2}[a-z]?\b/g, "");
  // Tidy what the removals left.
  out = out.replace(/\s+([,.;:])/g, "$1").replace(/,\s*,/g, ",").replace(/\(\s*\)/g, "").replace(/\s{2,}/g, " ");
  return out.trim();
}

/** The decision numbers and register ids a sentence carried, in order and once each - for the places that
 * keep them out of sight (a `data-references` attribute an auditor or the browser gate can read) while
 * the visible sentence is plain. A check that a sourced reason arrived needs a non-vacuous anchor, and
 * an id cannot appear in copy that merely denies the capability. */
export function internalReferences(text: string): string[] {
  const found = text.match(/\b(?:ADR-\d+|[A-Z]{1,4}-\d{2}[a-z]?)\b/g) ?? [];
  return [...new Set(found)];
}

/** A server sentence for a customer screen: the net, and the product's no-em-dash rule at the point of
 * display (a regulation's own label carries spaced dashes, and changing one is a `RegimeSpec.version`
 * bump, so the screen maps it). Words are the server's; only codes and punctuation change. */
export function customerText(text: string | null | undefined): string {
  if (!text) return "";
  return scrubInternal(text)
    .replace(/\s+[—–]\s+/g, " - ")
    .replace(/[—–]/g, "-");
}

/** A server sentence that carries a stored key (a disclosure key, a regime key) read aloud: the
 * underscores become spaces and nothing else changes. For the Activity sentences, whose clauses print a
 * regime key or a disclosure key as it is stored (`ledger/activity.py` keeps them raw on purpose), and
 * for no other text: prose has no underscores, so a hit anywhere else is itself the finding. */
export function speakCodes(text: string): string {
  return text.replace(/\b[A-Za-z][A-Za-z0-9]*(?:_[A-Za-z0-9]+)+\b/g, (code) => code.replace(/_/g, " "));
}

// ---------------------------------------------------------------------------------- support level

/** The five support levels of `regimes/support.py`, in the words a customer reads on a chip.
 *
 * **The level itself is `support.py`'s and is not restated or changed here**: the key is the label that
 * file publishes, the value is only how it is said. The fourth level is deliberately the one that says
 * what it does not do, because it is the level of both live regimes and the difference from the fifth
 * ("nothing sends a filing") is the sentence a finance lead needs before relying on it.
 * `test_customer_words_cover_the_server` fails if `support.py` gains a level this table has no words for. */
export const SUPPORT_LEVEL_WORDS: Readonly<Record<string, string>> = {
  "research tracked": "Tracked only",
  "preparation supported": "Preparation only",
  "quantitatively validated": "Figures validated",
  "complete reporting workflow supported": "Full workflow, no e-filing",
  "submission supported": "Full workflow with e-filing",
};

export function supportLevelWords(level: string | null | undefined): string {
  if (!level) return "Support not stated";
  return SUPPORT_LEVEL_WORDS[level.trim().toLowerCase()] ?? sentenceCase(level);
}

// ---------------------------------------------------------------------------------- ledger words

/** What a ledger entry type is called to a person. Keyed by the exact type the chain hashed.
 *
 * Short noun phrases (they head a table column), each saying what happened, none carrying a code.
 * `test_customer_words_cover_the_server` fails if `ledger/payloads.py` declares a type missing here. */
export const LEDGER_EVENT_WORDS: Readonly<Record<string, string>> = {
  "activity.attested": "Figure recorded on a statement",
  "activity.meter_chosen": "Supply point chosen",
  "activity.verified": "Figure accepted",
  applicability_determined: "Filing need decided",
  "approval.adjustment_requested": "Change requested",
  "approval.approved": "Approved",
  "approval.rejected": "Rejected",
  "approval_route.published": "Approval route published",
  "assistant.access_changed": "Assistant access changed",
  "assistant.cap_changed": "Assistant spending cap changed",
  "assistant.configured": "Assistant set up",
  "assistant.credential_replaced": "Assistant key replaced",
  "assistant.rates_changed": "Assistant rates changed",
  "assistant.switch_changed": "Assistant switched on or off",
  "assumptions.recorded": "Planning assumptions recorded",
  "assurance.request_raised": "Assurance request raised",
  "assurance.request_resolved": "Assurance request resolved",
  "assurance.request_withdrawn": "Assurance request withdrawn",
  "assurance.response_recorded": "Assurance answer recorded",
  calculation_created: "Figure calculated",
  calculation_superseded: "Figure recalculated",
  "connector.created": "Data feed added",
  "connector.credential_failed": "Data feed sign-in failed",
  "connector.credential_renewed": "Data feed sign-in renewed",
  "connector.credential_revoked": "Data feed sign-in withdrawn",
  correction: "Correction",
  disclosure_statement_written: "Disclosure statement written",
  "document.awaiting_review": "Document sent for review",
  "document.expired": "Document expired",
  "document.extracted": "Document read",
  "document.extracting": "Document being read",
  "document.import_meter_chosen": "Supply point chosen for an import",
  "document.imported": "Spreadsheet imported",
  "document.quarantined": "Document held back",
  "document.received": "Document received",
  "document.redacted": "Personal details removed from a document",
  "document.rejected": "Document rejected",
  "document.superseded": "Document replaced",
  "document.validated": "Document checked",
  "document.verified": "Document verified",
  "entity_financials.stated": "Company financials stated",
  "extraction.corrected": "Value corrected by a reviewer",
  "facility.amended": "Site amended",
  "facility.lease_answered": "Lease question answered",
  "facility.lease_revised": "Lease answer revised",
  "facility.provisioned": "Site added",
  "figure.imported": "Figure imported from a prior report",
  "imported.difference_resolved": "Difference explained",
  "initiative.achieved": "Initiative achieved",
  "initiative.added": "Initiative added",
  "inventory.bulk_applied": "Bulk edit applied",
  "inventory.bulk_meter_chosen": "Supply point chosen in a bulk edit",
  "legal_entity.amended": "Company amended",
  "legal_entity.provisioned": "Company added",
  "meter.provisioned": "Meter added",
  "meter.supply_account_assigned": "Meter linked to an account",
  "ownership.amended": "Ownership amended",
  "ownership.recorded": "Ownership recorded",
  "period.closed": "Period closed",
  "period.post_close_change": "Change after a period closed",
  "period.reopened": "Period reopened",
  "plan.decided": "Plan decision recorded",
  report_issued: "Filing issued",
  "report.reviewed": "Report reviewed",
  "reporting_period.provisioned": "Reporting period added",
  "risk.action_added": "Risk action added",
  "risk.action_completed": "Risk action completed",
  "risk.closed": "Risk closed",
  "risk.recorded": "Risk recorded",
  "risk.scale_replaced": "Risk scale replaced",
  "scope3.relevance_decided": "Scope 3 category decided",
  "sharing.granted": "Data sharing permitted",
  "sharing.revoked": "Data sharing withdrawn",
  "source.expected": "Expected bill added",
  "source.nil_declared": "Confirmed no bill",
  "source.nil_withdrawn": "No-bill confirmation withdrawn",
  "source.owner_assigned": "Owner assigned",
  "source.retired": "Expected bill ended",
  "structure.bulk_registered": "Sites and companies added in bulk",
  "supplier.accepted": "Supplier figure accepted",
  "supplier.request_raised": "Supplier asked for a figure",
  "supplier.submitted": "Supplier figure received",
  "supply_account.recorded": "Supply account added",
  "supply_account.retired": "Supply account ended",
  "target.reassessed": "Target reassessed",
  "target.set": "Target set",
  "user.erased": "Person's details removed",
  "vendor.recorded": "Vendor added",
  "vendor.renamed": "Vendor renamed",
  "vendor.restored": "Vendor restored",
  "vendor.retired": "Vendor ended",
};

/** A ledger entry type as a person reads it. The raw type belongs in a `title`, never as the value. */
export function ledgerEventWords(type: string): string {
  return LEDGER_EVENT_WORDS[type] ?? humanise(type);
}

/** What kind of thing a ledger entry is about (`filing_obligation`, `expected_source`). */
export const LEDGER_OBJECT_WORDS: Readonly<Record<string, string>> = {
  activity_record: "Figure",
  approval_route: "Approval route",
  assistant_config: "Assistant settings",
  assistant_credential: "Assistant key",
  assumption_set: "Planning assumptions",
  calculation: "Calculation",
  climate_risk: "Climate risk",
  data_sharing_agreement: "Data sharing",
  disclosure_statement: "Disclosure statement",
  document: "Document",
  entity_financials: "Company financials",
  expected_source: "Expected bill",
  extraction: "Document reading",
  facility: "Site",
  filing_obligation: "Filing",
  imported_difference_resolution: "Explained difference",
  imported_figure: "Imported figure",
  import: "Import",
  ingestion_connector: "Data feed",
  initiative: "Initiative",
  meter: "Meter",
  ownership: "Ownership",
  plan_decision: "Plan decision",
  report_artifact: "Issued filing",
  reporting_period: "Reporting period",
  risk_action: "Risk action",
  risk_scale: "Risk scale",
  scope3_assessment: "Scope 3 category",
  supplier_request: "Supplier request",
  supplier_submission: "Supplier figure",
  supply_account: "Supply account",
  transition_target: "Target",
  user_account: "Person",
  vendor: "Vendor",
};

export function ledgerObjectWords(objectType: string): string {
  return LEDGER_OBJECT_WORDS[objectType] ?? humanise(objectType);
}

/** Who acted, when the entry names a kind and no person. `engine no person` is the product itself. */
export const ACTOR_KIND_WORDS: Readonly<Record<string, string>> = {
  user: "Person",
  engine: "EmitIQ",
  extractor: "Document reader",
  scheduler: "Scheduled job",
};

export function actorKindWords(kind: string): string {
  return ACTOR_KIND_WORDS[kind] ?? humanise(kind);
}

/** The whole actor cell for an entry with no person: the system, said once rather than "engine no person". */
export function systemActorWords(kind: string): string {
  return kind === "user" ? "Person not recorded" : `${actorKindWords(kind)} (automatic)`;
}

// ---------------------------------------------------------------------------------- security history

/** The sign-in and role events of the security history (`AuthEventType`). */
export const SECURITY_EVENT_WORDS: Readonly<Record<string, string>> = {
  login_succeeded: "Signed in",
  login_failed: "Sign-in failed",
  mfa_succeeded: "Second step passed",
  mfa_failed: "Second step failed",
  mfa_enrolled: "Second step set up",
  logout: "Signed out",
  session_expired: "Session expired",
  session_revoked: "Session ended by an Admin",
  credential_locked: "Sign-in locked after failures",
  credential_changed: "Password or code changed",
  role_changed: "Role changed",
  claim_issued: "Invitation sent",
  claim_redeemed: "Invitation accepted",
  claim_refused: "Invitation refused",
  password_reset_requested: "Password reset asked for",
  password_reset_completed: "Password reset finished",
  password_reset_refused: "Password reset refused",
  invitation_resent: "Invitation sent again",
  invitation_revoked: "Invitation withdrawn",
  audit_export_generated: "Security history exported",
  membership_suspended: "Access suspended",
  membership_restored: "Access restored",
  membership_period_changed: "Access dates changed",
  mfa_policy_changed: "Second-step rule changed",
  password_reset_handled: "Password reset request handled",
};

export function securityEventWords(type: string): string {
  return SECURITY_EVENT_WORDS[type] ?? humanise(type);
}

/** The reason recorded beside a security event, when it is a code (`revoked_by_admin`). Free text a
 * person wrote is left alone: only a snake_case token is spoken. */
export function reasonWords(reason: string): string {
  return /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/.test(reason) ? humanise(reason) : reason;
}

// ---------------------------------------------------------------------------------- places and sets

/** The area part of a cloud region id, as words. **No region is named in this file**: a region id is the
 * estate's and has one configurable home (`noEnvironmentLiterals.test.ts`), so this speaks the *shape* of
 * an id (`<area>-<direction>-<n>`) and never a particular one. A place name ("London") would need a table
 * of regions, and that table would be the estate written into the SPA. */
const AREA_WORDS: Readonly<Record<string, string>> = {
  eu: "Europe",
  us: "North America",
  ap: "Asia Pacific",
  sa: "South America",
  me: "Middle East",
  af: "Africa",
};

/** Where the data is kept, as words: `<area> <direction> <n>`, with the provider. An id that is not
 * shaped like a region (a label an Admin typed) is shown as it is. */
export function regionWords(region: string): string {
  const m = /^([a-z]{2})-([a-z]+)-(\d)$/.exec(region);
  if (!m) return region;
  const [, area, direction, n] = m;
  return `${AREA_WORDS[area ?? ""] ?? (area ?? "").toUpperCase()} ${sentenceCase(direction ?? "")} ${n ?? ""} (AWS)`;
}

const PUBLISHER_WORDS: Readonly<Record<string, string>> = {
  epa: "EPA",
  desnz: "DESNZ",
  egrid: "eGRID",
  defra: "DEFRA",
  ipcc: "IPCC",
  iea: "IEA",
};

/** A factor set id spoken: `us_epa_2025_v2` is "US EPA 2025, version 2". */
export function factorSetWords(id: string): string {
  const m = /^([a-z]{2})_([a-z0-9]+)_(\d{4})(?:_v(\d+))?$/.exec(id);
  if (!m) return humanise(id);
  const [, country, publisher, year, version] = m;
  const who = `${(country ?? "").toUpperCase()} ${PUBLISHER_WORDS[publisher ?? ""] ?? (publisher ?? "").toUpperCase()}`;
  return version ? `${who} ${year ?? ""}, version ${version}` : `${who} ${year ?? ""}`;
}

/** The calculation engine's version as a customer reads it: "1.0.0" is "version 1.0". */
export function calculationVersionWords(engineVersion: string): string {
  const parts = engineVersion.split(".");
  return parts.length >= 2 ? `version ${parts[0] ?? ""}.${parts[1] ?? ""}` : `version ${engineVersion}`;
}

// ---------------------------------------------------------------------------------- operations

/** What an alert of the unattended work (`ops/alerts.py`) says, in a sentence. */
export const OPS_CODE_WORDS: Readonly<Record<string, string>> = {
  "ops.anchor_never_run": "The daily proof of the audit trail has never been made.",
  "ops.anchor_overdue": "The daily proof of the audit trail is late.",
  "ops.anchor_ahead_of_chain": "The saved proof is ahead of the audit trail it should describe.",
  "ops.extraction_stalled": "A document has been waiting to be read for too long.",
  "ops.extraction_age_unknown": "A document is being read and its start time was not recorded.",
};

export function opsCodeWords(code: string): string {
  return OPS_CODE_WORDS[code] ?? `${humanise(code.replace(/^ops\./, ""))}.`;
}

/** The fields an alert carries, named for a person. **Every field is still shown**: the panel prints
 * exactly the set the payload carried (a field going missing would be the screen choosing what an owner
 * may see), so this only changes what each is called. */
export const OPS_FIELD_WORDS: Readonly<Record<string, string>> = {
  org_id: "Workspace",
  document_id: "Document",
  head_seq: "Latest entry",
  entries: "Entries",
  last_anchored_seq: "Latest entry proved",
  unanchored_entries: "Entries waiting",
  waiting_hours: "Waiting (hours)",
  window_hours: "Allowed (hours)",
};

export function opsFieldWords(field: string): string {
  return OPS_FIELD_WORDS[field] ?? humanise(field);
}

// ---------------------------------------------------------------------------------- Admin states

/** The four states of an Admin section, in sentence case and in the words a customer uses.
 * `none_yet` and `not_built` stay apart (one is a state, the other a gap); `read_only` is "Read only" because
 * the point of the row is that it is shown and cannot be changed from here. */
export const ADMIN_STATE_WORDS: Readonly<Record<string, string>> = {
  settled: "Active",
  read_only: "Read only",
  none_yet: "Not set up",
  not_built: "Not available yet",
};

export function adminStateWords(state: string): string {
  return ADMIN_STATE_WORDS[state] ?? humanise(state);
}

// ---------------------------------------------------------------------------------- vocabulary

/** The build team's words, and what a customer says instead. Used by the screens that name the thing in
 * a template; kept as data so one change reaches every screen and a test can hold it. */
export const VOCABULARY = {
  /** The check of what bills were expected against what arrived. */
  census: "expected bills",
  censusAction: "Expected bills",
  /** One month or quarter of an expected bill. */
  window: "period",
  windows: "periods",
  missingWindows: "Missing bills",
  /** A person confirmed there is nothing to bill this period. */
  declaredNil: "Confirmed none",
  declaredNilLong: "A person confirmed no bill is expected",
  grossCv: "gross calorific value",
  submitRevision: "Send for approval",
} as const;

// ---------------------------------------------------------------------------------- settings rows

/** Served phrases that say a build state or a decision where a customer needs the plain fact, with what
 * to say instead. **Substring replacements, applied before the net**, each keyed on the exact words
 * `api/administration.py` serves - so a server sentence that is reworded stops matching and reaches the
 * screen as served, through the net, rather than as a stale sentence of ours.
 * `test_customer_words_cover_the_server` (backend) fails if a key here no longer occurs in what the
 * endpoint builds. The erasure and removal rows are deliberately NOT here: the screen pipes the served
 * sentence and authors none of its own (`Admin.test.tsx`, "what the screen says about erasure"). */
export const SERVED_PHRASES: ReadonlyArray<readonly [string, string]> = [
  ["Routes are versioned data (ADR-0037). A route is added by a new version", "A route is added by a new version"],
  [
    "No decision covers a federation provider, so nothing is built.",
    "Signing in through your own identity provider is not offered yet.",
  ],
  [
    "ADR-0022 (utility and energy data connectors) is written and awaiting a decision; it recommends email-in before any portal credential, and nothing is built.",
    "Connecting directly to utility and energy providers is not available yet. It is waiting on a decision; email-in comes first.",
  ],
  ["Each person can add passkeys under Settings;", "Each person can add passkeys on their profile;"],
  [
    "The server has been exercised only against a software authenticator, so this is not offered as a supported sign-in until a real-device run has been done.",
    "It has only been tried against software, so it is not offered as a supported way to sign in until it has been tried on a real device.",
  ],
];

/** The served *values* that are a build state, said as the customer says them. Exact match on the served
 * value, so a changed value reaches the screen as served. */
export const SERVED_VALUES: Readonly<Record<string, string>> = {
  "built, not yet proven on a real device": "In testing",
  "not available": "Not available yet",
};

function phrased(text: string): string {
  return SERVED_PHRASES.reduce((out, [from, to]) => out.split(from).join(to), text);
}

function supportOrServed(value: string): string {
  return SUPPORT_LEVEL_WORDS[value.trim().toLowerCase()] ?? value;
}

/** One Admin row as the customer reads it: the served value and note, with the table's words where it has
 * them, and the net over whatever is left. Nothing here is a second copy of a policy: the value and the
 * note are the server's, said without the codes. */
export function settingWords(
  sectionKey: string,
  label: string,
  served: { value: string; detail?: string | null },
): { value: string; detail: string } {
  let value: string;
  if (sectionKey === "governance" && label === "Region") value = regionWords(served.value);
  else if (sectionKey === "methodology" && label === "Factor editions")
    value = served.value.split(", ").map(factorSetWords).join(", ");
  else if (sectionKey === "methodology" && label === "Calculation engine")
    value = calculationVersionWords(served.value);
  else value = sentenceCase(SERVED_VALUES[served.value] ?? supportOrServed(served.value));
  return { value: scrubInternal(value), detail: scrubInternal(phrased(served.detail ?? "")) };
}

/** The section summary for a governance row names the region id (`in eu-west-2`); say the place. */
export function regionInSentence(text: string): string {
  return text.replace(/\b(?:eu|us)-(?:west|east|central)-\d\b/g, (id) => regionWords(id));
}

/** A served section's own summary or blocker, through the net, with the version of the engine and the
 * region said for a person. */
export function sectionSentence(text: string): string {
  return regionInSentence(
    scrubInternal(phrased(text).replace(/\bengine (\d+)\.(\d+)\.\d+/g, "calculation version $1.$2")),
  );
}
